import * as Icons from '@mui/icons-material';
import { Schedule } from '@mui/icons-material';
import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Typography,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { IWelcomeComponentProperties } from '@sharedInterfaces/IEmployeeTalkTemplate';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import type { AppState } from '@store/store';
import React from 'react';
import { useSelector } from 'react-redux';

import type { ITemplateComponentProps } from '../TemplateEditor/types';

import ETTBaseTemplateComponent from './ETTBaseTemplateComponent';

export interface IETTWelcomeComponentProps extends ITemplateComponentProps {}

const ONBOARDING_STEPS = [
    {
        icon: 'CloudUpload',
        title: 'CV Upload',
        description: 'Laden Sie Ihren Lebenslauf hoch',
        duration: '2 Min',
    },
    {
        icon: 'Assessment',
        title: 'KI-Analyse',
        description: 'Automatische Erkennung Ihrer Fähigkeiten',
        duration: '3 Min',
    },
    {
        icon: 'Visibility',
        title: 'Überprüfung',
        description: 'Bewerten und ergänzen Sie Ihre Daten',
        duration: '5 Min',
    },
    {
        icon: 'Celebration',
        title: 'Abschluss',
        description: 'Profil erstellen und Team benachrichtigen',
        duration: '1 Min',
    },
];

export const ETTWelcomeComponent: React.FC<IETTWelcomeComponentProps> = (props) => {
    const { component } = props;
    const properties = component.properties as IWelcomeComponentProperties;
    const theme = useTheme();

    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).employeeTalk;
    const employee = useSelector((state: AppState) => state.employee);
    const company = useSelector((state: AppState) => state.company);

    const firstName = employee?.firstName || 'Neuer Mitarbeiter';
    const companyName = company?.name || 'Ihrem Unternehmen';

    return (
        <ETTBaseTemplateComponent
            {...props}
            renderEditContent={() => (
                <Box sx={{ p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        Willkommen-Komponente (System-Komponente)
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Zeigt personalisierte Willkommensnachricht und Onboarding-Übersicht
                    </Typography>
                </Box>
            )}
            renderViewContent={() => (
                <Box sx={{ maxWidth: 800, mx: 'auto' }}>
                    {/* Robot Image */}
                    <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
                        <img src="/T4lo_Robot.png" alt="T4lo Robot" style={{ width: 150, height: 'auto' }} />
                    </Box>

                    {/* Welcome Header */}
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                            Willkommen, {firstName}!
                        </Typography>

                        <Typography variant="h5" color="text.secondary" sx={{ mb: 2 }}>
                            Schön, dass Sie bei {companyName} sind
                        </Typography>

                        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto', mb: 2 }}>
                            Lassen Sie uns gemeinsam Ihr Profil erstellen. Unser KI-Assistent wird Sie durch jeden
                            Schritt begleiten und dabei helfen, Ihre Fähigkeiten und Erfahrungen optimal zu
                            präsentieren.
                        </Typography>
                        {/* Start Button */}
                        <Box sx={{ textAlign: 'center', mt: 0 }}>
                            <Button
                                variant="contained"
                                size="large"
                                sx={{
                                    px: 6,
                                    py: 2,
                                    fontSize: '1.1rem',
                                    fontWeight: 600,
                                    borderRadius: 3,
                                    textTransform: 'none',
                                    background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                                    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.3)}`,
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.4)}`,
                                    },
                                    transition: 'all 0.3s ease',
                                    mb: 2,
                                }}
                                onClick={() => {
                                    if (props.onNext) {
                                        props.onNext();
                                    }
                                }}
                            >
                                Onboarding starten
                            </Button>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                            Sie können jederzeit pausieren und später fortfahren
                        </Typography>
                    </Box>

                    {/* Process Overview */}
                    <Card
                        elevation={0}
                        sx={{
                            mb: 4,
                            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                            background: alpha(theme.palette.background.paper, 0.7),
                        }}
                    >
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                                <Schedule color="primary" />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    Ihr Onboarding-Prozess
                                </Typography>
                                <Chip label="~11 Minuten" size="small" color="primary" variant="outlined" />
                            </Box>

                            <List sx={{ p: 0 }}>
                                {ONBOARDING_STEPS.map((step, index) => (
                                    <ListItem
                                        key={index}
                                        sx={{
                                            px: 0,
                                            py: 1.5,
                                            borderRadius: 1,
                                            '&:hover': {
                                                bgcolor: alpha(theme.palette.primary.main, 0.05),
                                            },
                                        }}
                                    >
                                        <ListItemIcon>
                                            <Avatar
                                                sx={{
                                                    width: 40,
                                                    height: 40,
                                                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                                                    color: theme.palette.primary.main,
                                                    fontSize: '1.2rem',
                                                }}
                                            >
                                                {(() => {
                                                    const IconComponent = (Icons as any)[step.icon];
                                                    return IconComponent ? <IconComponent /> : step.icon;
                                                })()}
                                            </Avatar>
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                    {step.title}
                                                </Typography>
                                            }
                                            secondary={step.description}
                                        />
                                        <Chip label={step.duration} size="small" variant="outlined" sx={{ ml: 2 }} />
                                    </ListItem>
                                ))}
                            </List>
                        </CardContent>
                    </Card>
                </Box>
            )}
        />
    );
};

export default ETTWelcomeComponent;
